<template>
  <div class="mt-5">
    <b-container fluid>
      <div class="card mb-3">
        <div class="card-body py-2 px-3">
          <div class="row align-items-end">
            <div class="col-md-6">
              <div class="form-group m-0 p-0">
                <label>Search Student (Email or Matric Number)</label>
                <div class="input-group">
                  <input
                    v-model="searchQuery"
                    @keyup.enter="searchStudent"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Enter student email or matric number"
                  />
                  <div class="input-group-append">
                    <button
                      class="btn btn-primary btn-sm"
                      @click="searchStudent"
                      :disabled="loading"
                    >
                      <b-spinner small v-if="loading"></b-spinner>
                      <i v-else class="fas fa-search"></i> Search
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="notFound" class="text-center text-muted my-4">
        <strong>No student found for "{{ lastSearch }}"</strong>
      </div>

      <template v-if="student">
        <div class="row mb-3">
          <div class="col-md-4">
            <div class="card">
              <div class="card-header bg-primary text-white">
                <strong>Student Information</strong>
              </div>
              <div class="card-body">
                <ul class="list-group list-group-flush">
                  <li class="list-group-item">
                    <strong>Name:</strong> {{ student.name }}
                  </li>
                  <li class="list-group-item">
                    <strong>Email:</strong> {{ student.email }}
                  </li>
                  <li class="list-group-item">
                    <strong>Matric No:</strong> {{ student.matric_number || "N/A" }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="col-md-8">
            <div class="row">
              <div class="col-md-4">
                <div class="small-box bg-success">
                  <div class="inner">
                    <h4>{{ formatCurrency(summary.total_paid) }}</h4>
                    <p>Total Paid</p>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="small-box bg-warning">
                  <div class="inner">
                    <h4>{{ formatCurrency(summary.total_pending) }}</h4>
                    <p>Pending</p>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="small-box bg-info">
                  <div class="inner">
                    <h4>{{ summary.transaction_count }}</h4>
                    <p>Transactions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Student Degrees -->
        <div class="card mb-3" v-if="degrees.length > 0">
          <div class="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
            <strong>Student Degrees</strong>
            <small>{{ degrees.length }} degree(s)</small>
          </div>
          <div class="card-body p-0">
            <b-table
              :items="degrees"
              :fields="degreeFields"
              small
              bordered
              hover
              show-empty
            >
              <template #cell(status)="{ item }">
                <b-badge :variant="item.status === 'CURRENT' ? 'success' : item.status === 'GRADUATED' ? 'primary' : 'warning'">
                  {{ item.status }}
                </b-badge>
              </template>
              <template #cell(admission_session)="{ value }">
                {{ value || 'N/A' }}
              </template>
              <template #cell(actions)="data">
                <button class="btn btn-sm btn-info mr-1" @click="openEditDegreeModal(data.item)">
                  <b-icon icon="pencil"></b-icon>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="confirmDeleteDegree(data.item)"
                  :disabled="deletingDegreeId === data.item.id"
                  v-if="isSuperAdmin"
                >
                  <b-spinner small v-if="deletingDegreeId === data.item.id"></b-spinner>
                  <b-icon icon="trash" v-else></b-icon>
                </button>
              </template>
            </b-table>
          </div>
        </div>

        <!-- Student Calendar -->
        <div class="card mb-3" v-if="calendar.length > 0 || degrees.length > 0">
          <div class="card-header d-flex justify-content-between align-items-center">
            <strong>Student Calendar (Semester Registrations)</strong>
            <button class="btn btn-sm btn-success" @click="openAddCalendarModal">
              <b-icon icon="plus-circle" class="mr-1"></b-icon> Add Semester
            </button>
          </div>
          <div class="card-body p-0">
            <b-table
              :items="calendar"
              :fields="calendarFields"
              small
              bordered
              hover
              show-empty
            >
              <template #cell(degree_info)="{ item }">
                {{ item.degree_name }} — {{ item.prog_code }}
              </template>
              <template #cell(actions)="data">
                <button
                  class="btn btn-sm btn-outline-danger"
                  @click="confirmDeleteCalendar(data.item)"
                  :disabled="deletingCalendarId === data.item.id"
                >
                  <b-spinner small v-if="deletingCalendarId === data.item.id"></b-spinner>
                  <b-icon icon="trash" v-else></b-icon>
                </button>
              </template>
            </b-table>
          </div>
          <div class="card-footer small text-muted">
            Year count is based on distinct sessions in this calendar for the current degree.
          </div>
        </div>

        <!-- Ledger Actions -->
        <div class="mb-3">
          <button class="btn btn-success btn-sm mr-1" @click="openEntryModal('CREDIT')">
            <b-icon icon="plus-circle" class="mr-1"></b-icon> Credit
          </button>
          <button class="btn btn-danger btn-sm mr-1" @click="openEntryModal('DEBIT')">
            <b-icon icon="dash-circle" class="mr-1"></b-icon> Debit
          </button>
          <button class="btn btn-info btn-sm mr-1" @click="openEntryModal('DISCOUNT')">
            <b-icon icon="tag" class="mr-1"></b-icon> Discount
          </button>
          <button class="btn btn-warning btn-sm" @click="openAssignBillModal">
            <b-icon icon="receipt" class="mr-1"></b-icon> Assign Bill
          </button>
        </div>

        <!-- Fee Schedule vs Payments -->
        <div class="card mb-3" v-if="billing">
          <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center">
            <strong>Fee Schedule vs Payments</strong>
            <small>{{ billing.prog_code }} | {{ billing.grant_type }} | {{ billing.degree }} | Year {{ billing.year_count }}</small>
          </div>
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-md-4">
                <div class="small-box bg-info">
                  <div class="inner">
                    <h4>{{ formatCurrency(billing.total_fees) }}</h4>
                    <p>Total Expected Fees</p>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="small-box bg-success">
                  <div class="inner">
                    <h4>{{ formatCurrency(billing.total_paid) }}</h4>
                    <p>Total Paid</p>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="small-box" :class="billing.balance > 0 ? 'bg-danger' : 'bg-success'">
                  <div class="inner">
                    <h4>{{ formatCurrency(billing.balance) }}</h4>
                    <p>{{ billing.balance > 0 ? 'Outstanding Balance' : 'Fully Paid' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div v-for="yearGroup in billing.fees_by_year" :key="yearGroup.year" class="mb-3">
              <h6 class="mb-1">Year {{ yearGroup.year }} <small class="text-muted">(Subtotal: {{ formatCurrency(yearGroup.subtotal) }})</small></h6>
              <b-table
                :items="yearGroup.items"
                :fields="feeItemFieldsWithAction"
                small
                bordered
                show-empty
              >
                <template #cell(amount)="{ value }">{{ formatCurrency(value) }}</template>
                <template #cell(session)="{ value }">{{ value || 'N/A' }}</template>
                <template #cell(exempt)="data">
                  <button
                    class="btn btn-sm btn-outline-warning"
                    @click="exemptFeeItem(data.item)"
                    :disabled="exemptingId === data.item.id"
                  >
                    <b-spinner small v-if="exemptingId === data.item.id"></b-spinner>
                    <b-icon icon="x-circle" v-else></b-icon>
                  </button>
                </template>
              </b-table>
            </div>

            <!-- Exempted Items -->
            <div v-if="billing.exempted_items && billing.exempted_items.length > 0" class="mt-3">
              <h6 class="text-muted"><b-icon icon="slash-circle" class="mr-1"></b-icon> Exempted Fee Items</h6>
              <b-table
                :items="billing.exempted_items"
                :fields="exemptedItemFields"
                small
                bordered
                class="table-secondary"
              >
                <template #cell(amount)="{ value }">
                  <del class="text-muted">{{ formatCurrency(value) }}</del>
                </template>
                <template #cell(restore)="data">
                  <button
                    class="btn btn-sm btn-outline-success"
                    @click="restoreFeeItem(data.item)"
                    :disabled="restoringId === data.item.exemption_id"
                  >
                    <b-spinner small v-if="restoringId === data.item.exemption_id"></b-spinner>
                    <b-icon icon="arrow-counterclockwise" v-else></b-icon>
                  </button>
                </template>
              </b-table>
            </div>
          </div>
        </div>
        <div class="alert alert-light small" v-else-if="student && billingError">
          <b-icon icon="info-circle" class="mr-1"></b-icon> {{ billingError }}
        </div>

        <!-- Student Transactions -->
        <div class="card mb-3">
          <div class="card-header">
            <strong>Student Payment Transactions</strong>
          </div>
          <div class="card-body p-0">
            <b-table
              :items="studentTransactions"
              :fields="studentTxFields"
              small
              bordered
              hover
              show-empty
              stacked="md"
            >
              <template #cell(amount)="{ value }">
                {{ formatCurrency(value) }}
              </template>
              <template #cell(status)="{ value }">
                <b-badge :variant="value === 'SUCCESS' ? 'success' : 'warning'">
                  {{ value }}
                </b-badge>
              </template>
              <template #cell(gateway)="{ value }">
                {{ (value || 'remita') === 'interswitch' ? 'Interswitch' : 'Remita' }}
              </template>
              <template #cell(payment_payload)="{ value }">
                <template v-if="Array.isArray(value)">
                  <div v-for="(item, i) in value" :key="i">
                    <small v-if="item">{{ item.type }}: ₦{{ item.amount }}</small>
                  </div>
                </template>
                <template v-else-if="value && typeof value === 'object'">
                  <small>{{ value.type }}</small>
                </template>
                <template v-else>
                  <small>{{ value }}</small>
                </template>
              </template>
              <template #cell(updated_at)="{ value }">
                {{ formatDate(value) }}
              </template>
              <template #cell(actions)="data">
                <button
                  v-if="data.item.status === 'PENDING' && isSuperAdmin"
                  class="btn btn-sm btn-outline-danger"
                  @click="confirmDeleteTx(data.item)"
                  :disabled="deletingTxId === data.item.id"
                >
                  <b-spinner small v-if="deletingTxId === data.item.id"></b-spinner>
                  <b-icon icon="trash" v-else></b-icon>
                </button>
              </template>
            </b-table>
          </div>
        </div>

        <!-- Applicant Transactions -->
        <div class="card mb-3" v-if="applicantTransactions.length > 0">
          <div class="card-header">
            <strong>Applicant Payment History</strong>
          </div>
          <div class="card-body p-0">
            <b-table
              :items="applicantTransactions"
              :fields="applicantTxFields"
              small
              bordered
              hover
              show-empty
              stacked="md"
            >
              <template #cell(amount)="{ value }">
                {{ formatCurrency(value) }}
              </template>
              <template #cell(status)="{ value }">
                <b-badge :variant="value === 'SUCCESS' ? 'success' : 'warning'">
                  {{ value }}
                </b-badge>
              </template>
              <template #cell(gateway)="{ value }">
                {{ (value || 'remita') === 'interswitch' ? 'Interswitch' : 'Remita' }}
              </template>
              <template #cell(updated_at)="{ value }">
                {{ formatDate(value) }}
              </template>
            </b-table>
          </div>
        </div>
      </template>

      <!-- Ledger Entry Modal -->
      <b-modal
        v-model="showEntryModal"
        :title="entryForm.type === 'CREDIT' ? 'Credit Student' : entryForm.type === 'DEBIT' ? 'Debit Student' : 'Add Discount'"
        @ok.prevent="postLedgerEntry"
        :ok-disabled="postingEntry"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="postingEntry" class="mr-1"></b-spinner>
          {{ postingEntry ? 'Posting...' : 'Post Entry' }}
        </template>

        <div class="alert alert-light small mb-3">
          <strong>Student:</strong> {{ student ? student.name : '' }} ({{ student ? student.matric_number || student.email : '' }})
        </div>

        <b-form-group label="Type">
          <b-form-select v-model="entryForm.type" :disabled="postingEntry">
            <option value="CREDIT">Credit (Payment received)</option>
            <option value="DEBIT">Debit (Charge / fine)</option>
            <option value="DISCOUNT">Discount (Fee reduction)</option>
          </b-form-select>
        </b-form-group>

        <b-form-group label="Amount (₦) *">
          <b-form-input type="number" v-model.number="entryForm.amount" :disabled="postingEntry" min="1" placeholder="Enter amount"></b-form-input>
        </b-form-group>

        <b-form-group label="Description *">
          <b-form-input v-model="entryForm.description" :disabled="postingEntry" placeholder="e.g. Bank transfer payment, Late registration fine, Scholarship discount"></b-form-input>
        </b-form-group>

        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Session">
              <b-form-select v-model="entryForm.session" :disabled="postingEntry">
                <option :value="null">-- Select --</option>
                <option v-for="s in sessionOptions" :key="s" :value="s">{{ s }}</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Degree">
              <b-form-input v-model="entryForm.degree" :disabled="postingEntry" :placeholder="billing ? billing.degree : 'e.g. MSc'"></b-form-input>
            </b-form-group>
          </div>
        </div>
      </b-modal>

      <!-- Assign Bill Modal -->
      <b-modal
        v-model="showAssignModal"
        title="Assign Bill Items to Student"
        size="lg"
        @ok.prevent="assignBill"
        :ok-disabled="assigningBill || selectedBillIds.length === 0"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="assigningBill" class="mr-1"></b-spinner>
          {{ assigningBill ? 'Assigning...' : 'Assign Selected (' + selectedBillIds.length + ')' }}
        </template>

        <div class="alert alert-light small mb-3">
          <strong>Student:</strong> {{ student ? student.name : '' }} ({{ student ? student.matric_number || student.email : '' }})
        </div>

        <div class="row mb-3">
          <div class="col-md-3">
            <label class="small mb-0">Session</label>
            <b-form-select v-model="billFilterSession" size="sm" @change="loadBillItems">
              <option value="">All</option>
              <option v-for="s in sessionOptions" :key="'bf-'+s" :value="s">{{ s }}</option>
            </b-form-select>
          </div>
          <div class="col-md-3">
            <label class="small mb-0">Prog Code</label>
            <v-select
              v-model="billFilterProgCode"
              :options="billProgCodes"
              placeholder="All"
              :clearable="true"
              class="vs-sm"
              @input="loadBillItems"
            ></v-select>
          </div>
          <div class="col-md-2">
            <label class="small mb-0">Year</label>
            <b-form-select v-model="billFilterYear" size="sm" @change="loadBillItems">
              <option value="">All</option>
              <option v-for="y in [1,2,3,4,5,6]" :key="y" :value="y">Year {{ y }}</option>
            </b-form-select>
          </div>
          <div class="col-md-4">
            <label class="small mb-0">Search</label>
            <b-form-input v-model="billFilter" size="sm" placeholder="Filter..."></b-form-input>
          </div>
        </div>

        <b-table
          :items="billItems"
          :fields="billItemFields"
          :busy="loadingBills"
          :filter="billFilter"
          small
          bordered
          hover
          show-empty
          :per-page="10"
          :current-page="billCurrentPage"
        >
          <template #table-busy>
            <div class="text-center my-3">
              <b-spinner small variant="primary"></b-spinner>
            </div>
          </template>
          <template #head(select)>
            <b-form-checkbox :checked="allBillsSelected" @change="toggleAllBills"></b-form-checkbox>
          </template>
          <template #cell(select)="data">
            <b-form-checkbox :checked="selectedBillIds.includes(data.item.id)" @change="toggleBillSelect(data.item.id)"></b-form-checkbox>
          </template>
          <template #cell(amount)="{ value }">
            {{ formatCurrency(value) }}
          </template>
          <template #cell(session)="{ value }">
            {{ value || 'N/A' }}
          </template>
          <template #cell(year)="{ value }">
            Year {{ value }}
          </template>
        </b-table>

        <b-pagination
          v-if="billItems.length > 10"
          v-model="billCurrentPage"
          :total-rows="billItems.length"
          :per-page="10"
          size="sm"
          class="mt-2"
        ></b-pagination>

        <div class="alert alert-info small mt-3 mb-0" v-if="selectedBillIds.length > 0">
          <strong>Total: {{ formatCurrency(selectedBillTotal) }}</strong> — {{ selectedBillIds.length }} item(s) will be assigned as pending transaction(s).
        </div>
      </b-modal>

      <!-- Edit Degree Modal -->
      <b-modal
        v-model="showDegreeModal"
        title="Edit Student Degree"
        @ok.prevent="saveStudentDegree"
        :ok-disabled="savingDegree"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="savingDegree" class="mr-1"></b-spinner>
          {{ savingDegree ? 'Saving...' : 'Save' }}
        </template>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Programme *">
              <v-select
                v-model="degreeForm.prog_id"
                :options="programmes"
                :get-option-label="o => o.programme + ' (' + (o.code || '') + ')'"
                :reduce="o => o.id"
                :filter="filterProgrammes"
                :disabled="savingDegree"
                placeholder="-- Select --"
              ></v-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Status *">
              <b-form-select v-model="degreeForm.status" :disabled="savingDegree">
                <option value="CURRENT">CURRENT</option>
                <option value="GRADUATED">GRADUATED</option>
                <option value="SUSPENDED">SUSPENDED</option>
                <option value="WITHDRAWN">WITHDRAWN</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Degree">
              <b-form-input v-model="degreeForm.degree" :disabled="savingDegree" placeholder="e.g. MSC, PGD, PHD"></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Prog Code">
              <b-form-input
                v-model="degreeForm.prog_code"
                :disabled="savingDegree"
                list="deg-prog-code-list"
                placeholder="e.g. STA*MSc*FT"
                autocomplete="off"
              ></b-form-input>
              <datalist id="deg-prog-code-list">
                <option v-for="p in programmes" :key="'dpc-'+p.id" :value="p.code">{{ p.programme }}</option>
              </datalist>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Grant Type">
              <b-form-select v-model="degreeForm.grant_type" :disabled="savingDegree">
                <option :value="null">-- Select --</option>
                <option value="RUN">RUN</option>
                <option value="TETFUND">TETFUND</option>
                <option value="OTHERS">OTHERS</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Admission Session">
              <b-form-select v-model="degreeForm.admission_session" :disabled="savingDegree">
                <option :value="null">-- None --</option>
                <option v-for="s in sessionOptions" :key="'das-'+s" :value="s">{{ s }}</option>
              </b-form-select>
            </b-form-group>
          </div>
        </div>
        <div class="alert alert-warning small mb-0" v-if="degreeForm.status === 'CURRENT'">
          Setting status to CURRENT will automatically change any other current degree to GRADUATED.
        </div>
      </b-modal>

      <!-- Add Calendar Modal -->
      <b-modal
        v-model="showCalendarModal"
        title="Add Semester to Student Calendar"
        @ok.prevent="addCalendarEntry"
        :ok-disabled="addingCalendar || !calendarForm.setting_id || !calendarForm.student_degree_id"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="addingCalendar" class="mr-1"></b-spinner>
          {{ addingCalendar ? 'Adding...' : 'Add' }}
        </template>
        <b-form-group label="Degree Programme *">
          <b-form-select v-model="calendarForm.student_degree_id" :disabled="addingCalendar">
            <option :value="null">-- Select degree --</option>
            <option v-for="d in degrees" :key="'cd-'+d.id" :value="d.id">
              {{ d.degree }} — {{ d.prog_code }} ({{ d.status }})
            </option>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Semester *">
          <b-form-select v-model="calendarForm.setting_id" :disabled="addingCalendar">
            <option :value="null">-- Select semester --</option>
            <option v-for="s in allSettings" :key="'cs-'+s.id" :value="s.id">
              {{ s.session_name }} — {{ s.semester_name }}
            </option>
          </b-form-select>
        </b-form-group>
      </b-modal>

    </b-container>
  </div>
</template>

<script>
function pad(n) {
  return n < 10 ? "0" + n : n;
}
export default {
  data() {
    return {
      loading: false,
      searchQuery: "",
      lastSearch: "",
      notFound: false,
      student: null,
      summary: {},
      billing: null,
      billingError: null,
      degrees: [],
      calendar: [],
      programmes: [],
      showDegreeModal: false,
      savingDegree: false,
      editDegreeId: null,
      deletingDegreeId: null,
      degreeForm: { prog_id: null, status: 'CURRENT', degree: '', prog_code: '', grant_type: null, admission_session: null },
      showCalendarModal: false,
      addingCalendar: false,
      deletingCalendarId: null,
      calendarForm: { student_degree_id: null, setting_id: null },
      studentTransactions: [],
      applicantTransactions: [],
      exemptingId: null,
      restoringId: null,
      feeItemFields: [
        { key: "item", label: "Fee Item" },
        { key: "amount", label: "Amount", class: "text-right" },
        { key: "installment", label: "Installment", class: "text-center" },
        { key: "session", label: "Session" },
      ],
      exemptedItemFields: [
        { key: "item", label: "Fee Item" },
        { key: "amount", label: "Amount", class: "text-right" },
        { key: "session", label: "Session" },
        { key: "exemption_reason", label: "Reason" },
        { key: "restore", label: "", class: "text-center", thStyle: { width: "60px" } },
      ],
      studentTxFields: [
        { key: "id", label: "ID", sortable: true },
        { key: "type", label: "Type", sortable: true },
        { key: "rrr", label: "RRR" },
        { key: "amount", label: "Amount", sortable: true },
        { key: "payment_payload", label: "Breakdown" },
        { key: "status", label: "Status", sortable: true },
        { key: "session", label: "Session", sortable: true },
        { key: "degree", label: "Degree", sortable: true },
        { key: "gateway", label: "Gateway", sortable: true },
        { key: "gateway_ref", label: "Description" },
        { key: "updated_at", label: "Date", sortable: true },
        { key: "actions", label: "", class: "text-center", thStyle: { width: "60px" } },
      ],
      deletingTxId: null,
      showEntryModal: false,
      postingEntry: false,
      showAssignModal: false,
      assigningBill: false,
      loadingBills: false,
      billItems: [],
      selectedBillIds: [],
      billFilterSession: "",
      billFilterProgCode: "",
      billFilterYear: "",
      billFilter: "",
      billCurrentPage: 1,
      billItemFields: [
        { key: "select", label: "", class: "text-center", thStyle: { width: "40px" } },
        { key: "item", label: "Fee Item", sortable: true },
        { key: "amount", label: "Amount", sortable: true, class: "text-right" },
        { key: "prog_code", label: "Prog Code", sortable: true },
        { key: "grant_type", label: "Grant Type", sortable: true },
        { key: "year", label: "Year", sortable: true, class: "text-center" },
        { key: "session", label: "Session", sortable: true },
        { key: "installment", label: "Installment", class: "text-center" },
      ],
      entryForm: {
        type: "CREDIT",
        amount: "",
        description: "",
        session: null,
        degree: "",
      },
      applicantTxFields: [
        { key: "type", label: "Type", sortable: true },
        { key: "rrr", label: "RRR" },
        { key: "amount", label: "Amount", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "gateway", label: "Gateway", sortable: true },
        { key: "updated_at", label: "Date", sortable: true },
      ],
      degreeFields: [
        { key: "degree", label: "Degree", sortable: true },
        { key: "prog_code", label: "Prog Code", sortable: true },
        { key: "programme_name", label: "Programme" },
        { key: "grant_type", label: "Grant Type", sortable: true },
        { key: "admission_session", label: "Admission Session", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "100px" } },
      ],
      calendarFields: [
        { key: "session_name", label: "Session", sortable: true },
        { key: "semester_name", label: "Semester", sortable: true },
        { key: "degree_info", label: "Degree / Programme" },
        { key: "actions", label: "", class: "text-center", thStyle: { width: "60px" } },
      ],
    };
  },
  computed: {
    settings() {
      return this.$store.state.settings || [];
    },
    sessionOptions() {
      if (!this.settings.length) return [];
      return [...new Set(this.settings.map((s) => s.session_name))];
    },
    feeItemFieldsWithAction() {
      return [
        ...this.feeItemFields,
        { key: "exempt", label: "", class: "text-center", thStyle: { width: "60px" } },
      ];
    },
    isSuperAdmin() {
      const roles = this.$store.state.user?.roles || [];
      return roles.includes("SUPERADMIN");
    },
    allSettings() {
      return (this.$store.state.settings || []).slice().sort((a, b) => {
        if (a.session_name !== b.session_name) return a.session_name > b.session_name ? -1 : 1;
        return a.semester_name > b.semester_name ? -1 : 1;
      });
    },
    allBillsSelected() {
      return this.billItems.length > 0 && this.selectedBillIds.length === this.billItems.length;
    },
    billProgCodes() {
      return [...new Set(this.billItems.map((i) => i.prog_code))].sort();
    },
    selectedBillTotal() {
      return this.billItems.filter((i) => this.selectedBillIds.includes(i.id)).reduce((sum, i) => sum + (i.amount || 0), 0);
    },
  },
  created() {
    this.$store.dispatch("getSettings");
    this.loadProgrammes();
  },
  methods: {
    formatCurrency(val) {
      return "₦" + Number(val || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 });
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}`;
    },
    loadBilling(studentId) {
      axios
        .get("admin/bursary/student-billing", {
          params: { student_id: studentId },
        })
        .then((res) => {
          if (res.data.msg === "success") {
            this.billing = res.data;
          } else {
            this.billingError = res.data.message || "Could not load billing info";
          }
        })
        .catch((err) => {
          this.billingError = err.response?.data?.message || "Billing info not available";
        });
    },
    exemptFeeItem(feeItem) {
      Swal.fire({
        title: "Exempt this fee item?",
        text: '"' + feeItem.item + '" — ' + this.formatCurrency(feeItem.amount) + '. Student will no longer see this item.',
        icon: "question",
        input: "text",
        inputLabel: "Reason (optional)",
        inputPlaceholder: "e.g. Scholarship, staff ward, etc.",
        showCancelButton: true,
        confirmButtonText: "Exempt",
      }).then((result) => {
        if (result.isConfirmed) {
          this.exemptingId = feeItem.id;
          axios.post("admin/bursary/fee-exemptions", {
            student_id: this.student.id,
            fee_schedule_id: feeItem.id,
            reason: result.value || null,
          })
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: res.data.message });
                this.loadBilling(this.student.id);
              } else {
                Toast.fire({ icon: "error", title: res.data.message || "Failed" });
              }
            })
            .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to exempt" }))
            .finally(() => (this.exemptingId = null));
        }
      });
    },
    restoreFeeItem(feeItem) {
      Swal.fire({
        title: "Restore this fee item?",
        text: '"' + feeItem.item + '" will appear on the student\'s bill again.',
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Restore",
      }).then((result) => {
        if (result.isConfirmed) {
          this.restoringId = feeItem.exemption_id;
          axios.delete("admin/bursary/fee-exemptions/" + feeItem.exemption_id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: res.data.message });
                this.loadBilling(this.student.id);
              }
            })
            .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to restore" }))
            .finally(() => (this.restoringId = null));
        }
      });
    },
    confirmDeleteTx(tx) {
      Swal.fire({
        title: "Remove this bill?",
        text: '"' + (tx.gateway_ref || tx.type) + '" — ' + this.formatCurrency(tx.amount),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, remove",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deletingTxId = tx.id;
          axios.delete("admin/bursary/student-transaction/" + tx.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "Bill removed" });
                this.searchStudent();
              }
            })
            .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to remove" }))
            .finally(() => (this.deletingTxId = null));
        }
      });
    },
    openAssignBillModal() {
      this.selectedBillIds = [];
      this.billFilterSession = "";
      this.billFilterProgCode = "";
      this.billFilterYear = "";
      this.billFilter = "";
      this.billCurrentPage = 1;
      if (this.billing) {
        this.billFilterProgCode = this.billing.prog_code || "";
      }
      this.showAssignModal = true;
      this.loadBillItems();
    },
    loadBillItems() {
      this.loadingBills = true;
      this.selectedBillIds = [];
      let params = {};
      if (this.billFilterSession) params.session = this.billFilterSession;
      if (this.billFilterProgCode) params.prog_code = this.billFilterProgCode;
      if (this.billFilterYear) params.year = this.billFilterYear;
      axios.get("admin/fee-schedules", { params })
        .then((res) => {
          this.billItems = (res.data.data || []);
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load fee schedule items" }))
        .finally(() => (this.loadingBills = false));
    },
    toggleBillSelect(id) {
      const idx = this.selectedBillIds.indexOf(id);
      if (idx > -1) {
        this.selectedBillIds.splice(idx, 1);
      } else {
        this.selectedBillIds.push(id);
      }
    },
    toggleAllBills(checked) {
      this.selectedBillIds = checked ? this.billItems.map((i) => i.id) : [];
    },
    assignBill() {
      if (!this.selectedBillIds.length) return;
      this.assigningBill = true;
      axios.post("admin/bursary/assign-bill", {
        student_id: this.student.id,
        fee_schedule_ids: this.selectedBillIds,
      })
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: res.data.message });
            this.showAssignModal = false;
            this.searchStudent();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Failed" });
          }
        })
        .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to assign bill" }))
        .finally(() => (this.assigningBill = false));
    },
    openEntryModal(type) {
      this.entryForm = {
        type: type,
        amount: "",
        description: "",
        session: null,
        degree: this.billing ? this.billing.degree : "",
      };
      this.showEntryModal = true;
    },
    postLedgerEntry() {
      if (!this.entryForm.amount || !this.entryForm.description) {
        Toast.fire({ icon: "warning", title: "Amount and description are required" });
        return;
      }
      this.postingEntry = true;
      axios
        .post("admin/bursary/ledger-entry", {
          student_id: this.student.id,
          type: this.entryForm.type,
          amount: this.entryForm.amount,
          description: this.entryForm.description,
          session: this.entryForm.session,
          degree: this.entryForm.degree,
        })
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: res.data.message });
            this.showEntryModal = false;
            this.searchStudent();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Failed" });
          }
        })
        .catch((err) => {
          Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to post entry" });
        })
        .finally(() => (this.postingEntry = false));
    },
    loadProgrammes() {
      axios.get("admin/programmes").then((res) => {
        if (res.data.msg === "success") this.programmes = res.data.data;
      }).catch(() => {});
    },
    filterProgrammes(options, search) {
      var s = search.toLowerCase();
      return options.filter(function (o) {
        return (o.programme || '').toLowerCase().indexOf(s) > -1
          || (o.code || '').toLowerCase().indexOf(s) > -1
          || (o.degree || '').toLowerCase().indexOf(s) > -1;
      });
    },
    openEditDegreeModal(deg) {
      this.editDegreeId = deg.id;
      this.degreeForm = {
        prog_id: deg.prog_id,
        status: deg.status,
        degree: deg.degree || '',
        prog_code: deg.prog_code || '',
        grant_type: deg.grant_type || null,
        admission_session: deg.admission_session || null,
      };
      this.showDegreeModal = true;
    },
    saveStudentDegree() {
      if (!this.degreeForm.prog_id) {
        Toast.fire({ icon: "warning", title: "Programme is required" });
        return;
      }
      this.savingDegree = true;
      axios.put("admin/students/" + this.student.id + "/degrees/" + this.editDegreeId, this.degreeForm)
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: "Degree updated" });
            this.showDegreeModal = false;
            this.searchStudent();
          }
        })
        .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to update" }))
        .finally(() => (this.savingDegree = false));
    },
    confirmDeleteDegree(deg) {
      Swal.fire({
        title: "Delete this degree?",
        text: deg.degree + " — " + deg.prog_code + ". This will also delete all calendar entries for this degree.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deletingDegreeId = deg.id;
          axios.delete("admin/students/" + this.student.id + "/degrees/" + deg.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "Degree deleted" });
                this.searchStudent();
              }
            })
            .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to delete" }))
            .finally(() => (this.deletingDegreeId = null));
        }
      });
    },
    openAddCalendarModal() {
      const currentDeg = this.degrees.find((d) => d.status === "CURRENT");
      this.calendarForm = {
        student_degree_id: currentDeg ? currentDeg.id : null,
        setting_id: null,
      };
      this.showCalendarModal = true;
    },
    addCalendarEntry() {
      if (!this.calendarForm.setting_id || !this.calendarForm.student_degree_id) return;
      this.addingCalendar = true;
      axios.post("admin/students/" + this.student.id + "/calendar", this.calendarForm)
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: "Semester added" });
            this.showCalendarModal = false;
            this.searchStudent();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Failed" });
          }
        })
        .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to add semester" }))
        .finally(() => (this.addingCalendar = false));
    },
    confirmDeleteCalendar(cal) {
      Swal.fire({
        title: "Remove this semester?",
        text: cal.session_name + " — " + cal.semester_name + ". This will affect the student's year count.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, remove",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deletingCalendarId = cal.id;
          axios.delete("admin/students/" + this.student.id + "/calendar/" + cal.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "Semester removed" });
                this.searchStudent();
              }
            })
            .catch((err) => Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to remove" }))
            .finally(() => (this.deletingCalendarId = null));
        }
      });
    },
    searchStudent() {
      if (!this.searchQuery.trim()) return;
      this.loading = true;
      this.notFound = false;
      this.student = null;
      this.billing = null;
      this.billingError = null;
      this.degrees = [];
      this.calendar = [];
      this.lastSearch = this.searchQuery.trim();

      axios
        .get("admin/bursary/student-ledger", {
          params: { search: this.searchQuery.trim() },
        })
        .then((response) => {
          if (response.data.msg === "success") {
            this.student = response.data.student;
            this.summary = response.data.summary;
            this.studentTransactions = response.data.student_transactions || [];
            this.applicantTransactions = response.data.applicant_transactions || [];
            this.degrees = response.data.degrees || [];
            this.calendar = response.data.calendar || [];
            if (this.student && this.student.id) {
              this.loadBilling(this.student.id);
            }
          } else {
            this.notFound = true;
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 404) {
            this.notFound = true;
          } else {
            Toast.fire({ icon: "error", title: "Failed to fetch student ledger" });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.small-box {
  border-radius: 0.25rem;
  padding: 20px;
  color: #fff;
  margin-bottom: 10px;
}
.small-box .inner h4 {
  font-size: 1.4rem;
  font-weight: 700;
}
.small-box .inner p {
  margin: 0;
  font-size: 0.85rem;
}
.vs-sm >>> .vs__dropdown-toggle {
  min-height: 31px;
  font-size: 0.875rem;
  padding: 0;
}
.vs-sm >>> .vs__search {
  font-size: 0.875rem;
  margin: 2px 0 2px 4px;
}
.vs-sm >>> .vs__selected {
  font-size: 0.875rem;
  margin: 2px 4px;
}
</style>
